import React, { useEffect, useState } from 'react'
import {
    Box,
    Typography,
    Grid,
    Link,
    Dialog,
    DialogContent,
    styled,
} from '@mui/material'
import { ReactComponent as CloseIconSvg } from 'icons/close-icon.svg'
import CustomButton from 'components/CustomButton'
import { useTranslation } from 'react-i18next'
import {
    MUIDateTimePicker,
    NativeDateTimePicker,
} from 'packages/eid-ui/DateTimePickers'
import moment from 'moment'
import { Icon } from 'packages/eid-icons'
import ArrowIcon from './ArrowIcon'
import { OutOfOffceSelector } from './OutOfOfficeSelector'

const StyledModal = styled(Dialog)({
    '-webkit-backdrop-filter': 'blur(4px)',
    backdropFilter: 'blur(4px)',
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
})

const StyledModalContentWrapper = styled(DialogContent)(({ theme }) => ({
    padding: '27px 46px 54px 50px',
    background: '#f7f8fa',
    width: '100%',
    maxWidth: 960,
    border: 'solid 1px #ebebed',
    borderRadius: '5px',
    boxShadow: '0 0 13px 0 rgba(0, 0, 0, 0.15)',
    [theme.breakpoints.down('sm')]: {
        padding: '27px 30px 54px 30px',
    },
}))

const StyledCloseButton = styled(Box)({
    position: 'absolute',
    top: 16,
    right: 16,
})

const StyledOutModalCheckLabel = styled('label')({
    marginLeft: '10px',
    fontSize: '14px',
    color: '#000',
})

const StyledOutModalInner = styled(Box)({
    padding: '12px 17px',
    marginTop: '16px',
    borderRadius: '5px',
    border: '1px solid #e4e5e6',
    backgroundColor: '#f2f3f4',
    width: '100%',
})

const StyledOutModalInnerText = styled(Typography)({
    fontSize: '14px',
    color: '#000',
    marginBottom: '10px',
})

const StyledModalContent = styled(Box)({
    padding: '35px 0px 42px 0px',
    minHeight: '325px',
})

const StyledOutModalTextArea = styled('textarea')({
    border: 'solid 1px #d8d8dd',
    fontSize: '14px',
    color: '#3b454d',
    padding: '8px',
    lineHeight: '1.71',
    boxShadow: '0 4px 6px -2px rgba(0, 0, 0, 0.1)',
    width: '100%',
})

const StyledDateTimeBox = styled(Box)({
    border: 'solid 1px #d8d8dd',
    fontSize: '14px',
    color: '#3b454d',
    lineHeight: '1.71',
    boxShadow: '0 4px 6px -2px rgba(0, 0, 0, 0.1)',
    width: '100%',
    backgroundColor: '#fff',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '20px',
})

const StyledHeaderTypo = styled(Typography)({
    fontWeight: 'bold',
    marginLeft: 9,
})

const StyledPaddingBox = styled(Box)({
    paddingLeft: '25px',
})

const StyledMarginBox = styled(Box)({
    marginBottom: '15px',
    marginTop: '15px',
})

const StyledDateTimeLabel = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'selected',
})(({ selected }) => ({
    display: 'flex',
    '& label': {
        color: selected ? '#000' : '#767676',
    },
    '& input': {
        color: selected ? '#000' : '#767676',
    },
}))

const StyledEndTime = styled(Box)({
    display: 'flex',
    alignItems: 'end',
})

const StyledStartTime = styled(Box)({
    display: 'flex',
    alignItems: 'end',
    svg: {
        margin: '10px',
    },
})

const MessageTypography = styled(Typography)(({ theme }) => ({
    fontSize: '1.4rem',
    fontWeight: 'normal',
    padding: '0 1rem 2rem',
    '&.MuiTypography-body1': {
        color: '#028f09',
    },
    '&.MuiTypography-body2': {
        color: '#ff0000',
    },
}))

export default function OutOfOfficeModal({
    open,
    onClose,
    loading,
    oofInfo,
    updateData,
    handleDiscard,
    actions,
    unsavedChanges,
    response,
    handleOoFFieldChange,
    setResponse,
}) {
    const { t } = useTranslation()
    const [errorMesssage, setErrorMessage] = useState('')
    const [ooOselectionType, setOoOSelectionType] = useState(
        oofInfo.isOutOfOffice ? 'ooOffice' : 'inOffice',
    )
    const [selectedStartDate, setSelectedStartDate] = useState(
        oofInfo.oofStartDate
            ? moment.utc(oofInfo.oofStartDate).local().format('LLL')
            : null,
    )
    const [selectedEndDate, setSelectedEndDate] = useState(
        oofInfo.oofEndDate
            ? moment.utc(oofInfo.oofEndDate).local().format('LLL')
            : null,
    )
    const [chooseAudience, setChooseAudience] = useState(
        oofInfo.oofAudience === null ||
            oofInfo.oofAudience === 'none' ||
            oofInfo.oofAudience === 'None'
            ? false
            : true,
    )
    const [isOofEnabled, setIsOofEnabled] = useState(oofInfo.isOutOfOffice)
    const handleOnChange = (e) => {
        handleOoFFieldChange({
            key: 'oofStatus',
            value: e.target.checked ? 'Scheduled' : 'AlwaysEnabled',
        })
    }

    useEffect(() => {
        setErrorMessage('')
    }, [response])

    const handleOOfChange = (e) => {
        if (response) setResponse(undefined)
        setIsOofEnabled(e.target.checked)
        setErrorMessage('')
        if (!e.target.checked)
            handleOoFFieldChange({ key: 'oofStatus', value: 'Disabled' })
    }

    const handleInputChange = (e) => {
        if (response) setResponse(undefined)
        setErrorMessage('')
        const { name, value } = e.target
        handleOoFFieldChange({ key: name, value: value })
    }

    const handleChooseAudience = (e) => {
        if (response) setResponse(undefined)
        setErrorMessage('')
        setChooseAudience(e.target.checked)
        handleOoFFieldChange({
            key: 'oofAudience',
            value: e.target.checked ? 'ContactsOnly' : 'none',
        })
    }

    const handleDateChange = (type, date) => {
        if (response) setResponse(undefined)
        setErrorMessage('')
        handleOoFFieldChange({
            key: type,
            value: date
                ? moment(date).utc().format('YYYY-MM-DDTHH:mm:ss')
                : null,
        })
    }

    const handleOoOChange = (e) => {
        if (response) setResponse(undefined)
        setErrorMessage('')
        if (!oofInfo.isOutOfOffice) {
            setChooseAudience(true)
            setIsOofEnabled(true)
        } else {
            setChooseAudience(false)
            setIsOofEnabled(false)
        }
        handleOoFFieldChange({
            key: 'isOutOfOffice',
            value: !oofInfo.isOutOfOffice,
        })

        setOoOSelectionType(e)
    }

    const validateOnSubmit = () => {
        if (response) setResponse(undefined)
        let isValidForm = true
        if (oofInfo.oofStatus === 'Scheduled') {
            if (oofInfo.oofStartDate && oofInfo.oofEndDate) {
                //Check for valid end date
                if (!moment(oofInfo.oofEndDate).isAfter(oofInfo.oofStartDate)) {
                    isValidForm = false
                    setErrorMessage(t('OoO_EndDate_Error'))
                }
            } else {
                isValidForm = false
                setErrorMessage(t('OoO_Valid_Time_Error'))
            }
        }
        if (isValidForm) {
            if (
                chooseAudience &&
                (oofInfo.oofAudience === 'none' ||
                    oofInfo.oofAudience === 'None')
            ) {
                isValidForm = false
                setErrorMessage(t('OoO_Valid_Audience_Error'))
            } else if (
                oofInfo.oofAudience &&
                oofInfo.oofAudience !== 'none' &&
                oofInfo.oofAudience !== 'None' &&
                !oofInfo.oofExternalMsg
            ) {
                isValidForm = false
                setErrorMessage(t('OoO_Valid_Audience_Msg_Error'))
            } else if (oofInfo.oofAudience === null) {
                handleOoFFieldChange({ key: 'oofAudience', value: 'none' })
            }
        }

        if (isValidForm) {
            setErrorMessage('')
            updateData()
        }
    }

    return (
        <div>
            <StyledModal
                aria-labelledby="simple-modal-title"
                maxWidth="md"
                open={open}
                onClose={onClose}
                scroll="body"
            >
                <StyledModalContentWrapper>
                    <Box>
                        <StyledHeaderTypo id="simple-modal-title" variant="h5">
                            {t('SetOoOStatus')}
                        </StyledHeaderTypo>
                        <StyledCloseButton>
                            <Link
                                component="button"
                                onClick={onClose}
                                aria-label="Close"
                                data-ai-explain="Close this dialog"
                            >
                                <CloseIconSvg></CloseIconSvg>
                            </Link>
                        </StyledCloseButton>
                    </Box>
                    <Box>
                        <OutOfOffceSelector
                            value={ooOselectionType}
                            handleChange={(e) => handleOoOChange(e)}
                        />
                    </Box>

                    <StyledModalContent>
                        <Box>
                            <input
                                id="oof-auto-reply"
                                disabled={!oofInfo.isOutOfOffice}
                                checked={isOofEnabled}
                                type="checkbox"
                                onChange={(e) => handleOOfChange(e)}
                            />
                            <StyledOutModalCheckLabel htmlFor="oof-auto-reply">
                                {`${t('Send_Auto_Reply')} "${oofInfo.email}"`}
                            </StyledOutModalCheckLabel>
                        </Box>

                        <StyledOutModalInner>
                            <StyledOutModalInnerText>
                                {t('SetOoOStatusMsg')}
                            </StyledOutModalInnerText>
                            <StyledOutModalTextArea
                                id="oofInternalMsg"
                                disabled={
                                    !isOofEnabled || !oofInfo.isOutOfOffice
                                }
                                rows="4"
                                name="oofInternalMsg"
                                aria-label="Set out of office status message"
                                onChange={(e) => handleInputChange(e)}
                                value={
                                    oofInfo.oofInternalMsg
                                        ? oofInfo.oofInternalMsg
                                        : `${t('OoOInternalMsg')} ${
                                              oofInfo.mobilePhone
                                                  ? oofInfo.mobilePhone
                                                  : '+1988888xxxx'
                                          }`
                                }
                                placeholder={`${t('OoOInternalMsg')} ${
                                    oofInfo.mobilePhone
                                }`}
                            ></StyledOutModalTextArea>

                            <StyledMarginBox>
                                <input
                                    id="oof-scheduled"
                                    checked={oofInfo.oofStatus === 'Scheduled'}
                                    disabled={
                                        !isOofEnabled || !oofInfo.isOutOfOffice
                                    }
                                    name="Scheduled"
                                    type="checkbox"
                                    onChange={(e) => handleOnChange(e)}
                                />
                                <StyledOutModalCheckLabel
                                    htmlFor="oof-scheduled"
                                    style={{
                                        color: '#000',
                                    }}
                                >
                                    {t('OoOScheduleMsg')}
                                </StyledOutModalCheckLabel>
                            </StyledMarginBox>
                            <StyledDateTimeBox
                                style={{
                                    opacity: `${
                                        isOofEnabled &&
                                        oofInfo.oofStatus === 'Scheduled' &&
                                        oofInfo.isOutOfOffice
                                            ? 1
                                            : 0.3
                                    }`,
                                }}
                            >
                                <StyledStartTime>
                                    <Icon
                                        name={'StartTime'}
                                        style={{
                                            marginRight: '10px',
                                            marginBottom: '6px',
                                            width: '20px',
                                        }}
                                    />
                                    <Box>
                                        <label
                                            id="oof-start-time-label"
                                            style={{
                                                fontSize: '12px',
                                                color: '#919193',
                                            }}
                                        >
                                            {t('OoOStartTime')}
                                        </label>
                                        <MUIDateTimePicker
                                            dateTimeFormat="YYYY-MM-DD HH:mm"
                                            fieldProps={{
                                                inputProps: {
                                                    'aria-labelledby':
                                                        'oof-start-time-label',
                                                },
                                            }}
                                            value={
                                                selectedStartDate
                                                    ? moment(selectedStartDate)
                                                    : null
                                            }
                                            handleChange={(e) => {
                                                handleDateChange(
                                                    'oofStartDate',
                                                    e,
                                                )
                                                setSelectedStartDate(
                                                    moment(e).format(
                                                        'YYYY-MM-DDTHH:mm',
                                                    ),
                                                )
                                            }}
                                            minDate={moment()}
                                            disabled={
                                                !isOofEnabled ||
                                                oofInfo.oofStatus !==
                                                    'Scheduled' ||
                                                !oofInfo.isOutOfOffice
                                            }
                                        ></MUIDateTimePicker>
                                    </Box>
                                </StyledStartTime>
                                <ArrowIcon
                                    color={'#5d6870'}
                                    style={{ margin: '0px 20px' }}
                                />
                                <StyledEndTime>
                                    <Icon
                                        name={'EndTime'}
                                        style={{
                                            marginRight: '10px',
                                            marginBottom: '6px',
                                            width: '20px',
                                        }}
                                    />
                                    <Box>
                                        <label
                                            id="oof-end-time-label"
                                            style={{
                                                fontSize: '12px',
                                                color: '#919193',
                                            }}
                                        >
                                            {t('OoOEndTime')}
                                        </label>
                                        <MUIDateTimePicker
                                            dateTimeFormat="YYYY-MM-DD HH:mm"
                                            fieldProps={{
                                                inputProps: {
                                                    'aria-labelledby':
                                                        'oof-end-time-label',
                                                },
                                            }}
                                            value={
                                                selectedEndDate
                                                    ? moment(selectedEndDate)
                                                    : null
                                            }
                                            handleChange={(e) => {
                                                handleDateChange(
                                                    'oofEndDate',
                                                    e,
                                                )
                                                setSelectedEndDate(
                                                    moment(e).format(
                                                        'YYYY-MM-DDTHH:mm',
                                                    ),
                                                )
                                            }}
                                            minDate={
                                                selectedStartDate
                                                    ? moment(selectedStartDate)
                                                    : moment()
                                            }
                                            disabled={
                                                !isOofEnabled ||
                                                oofInfo.oofStatus !==
                                                    'Scheduled' ||
                                                !oofInfo.isOutOfOffice
                                            }
                                        ></MUIDateTimePicker>
                                    </Box>
                                </StyledEndTime>
                            </StyledDateTimeBox>

                            <Box
                                style={{
                                    marginTop: '15px',
                                }}
                            >
                                <input
                                    checked={chooseAudience}
                                    onChange={(e) => handleChooseAudience(e)}
                                    disabled={
                                        !isOofEnabled || !oofInfo.isOutOfOffice
                                    }
                                    type="checkbox"
                                />
                                <StyledOutModalCheckLabel
                                    style={{
                                        color: '#000',
                                    }}
                                >
                                    {t('OoOReplyOutSide')}
                                </StyledOutModalCheckLabel>
                            </Box>
                            <StyledPaddingBox>
                                <StyledDateTimeLabel
                                    selected={
                                        oofInfo.oofAudience === 'ContactsOnly'
                                    }
                                >
                                    <input
                                        checked={
                                            oofInfo.oofAudience ===
                                            'ContactsOnly'
                                        }
                                        onChange={(e) => handleInputChange(e)}
                                        disabled={
                                            !isOofEnabled ||
                                            !chooseAudience ||
                                            !oofInfo.isOutOfOffice
                                        }
                                        type="radio"
                                        value="ContactsOnly"
                                        name="oofAudience"
                                    />
                                    <StyledOutModalCheckLabel>
                                        {t('OoOMyContacts')}
                                    </StyledOutModalCheckLabel>
                                </StyledDateTimeLabel>
                                <StyledDateTimeLabel
                                    selected={oofInfo.oofAudience === 'All'}
                                >
                                    <input
                                        checked={oofInfo.oofAudience === 'All'}
                                        onChange={(e) => handleInputChange(e)}
                                        disabled={
                                            !isOofEnabled ||
                                            !chooseAudience |
                                                !oofInfo.isOutOfOffice
                                        }
                                        type="radio"
                                        value="All"
                                        name="oofAudience"
                                    />
                                    <StyledOutModalCheckLabel>
                                        {t('OoOAllExternals')}
                                    </StyledOutModalCheckLabel>
                                </StyledDateTimeLabel>

                                <StyledOutModalInnerText
                                    style={{
                                        marginTop: '15px',
                                        color: '#000',
                                    }}
                                >
                                    {`${t('OoOExternamMsg')}:`}
                                </StyledOutModalInnerText>
                                <StyledOutModalTextArea
                                    disabled={
                                        !isOofEnabled ||
                                        !chooseAudience ||
                                        !oofInfo.isOutOfOffice
                                    }
                                    rows="3"
                                    name="oofExternalMsg"
                                    onChange={(e) => handleInputChange(e)}
                                    value={
                                        oofInfo.oofAudience === null ||
                                        oofInfo.oofAudience === 'none' ||
                                        oofInfo.oofAudience === 'None'
                                            ? ''
                                            : oofInfo.oofExternalMsg
                                    }
                                ></StyledOutModalTextArea>
                            </StyledPaddingBox>
                        </StyledOutModalInner>
                    </StyledModalContent>
                    <Box>
                        <MessageTypography
                            variant={
                                response &&
                                response.success &&
                                errorMesssage === ''
                                    ? 'body1'
                                    : 'body2'
                            }
                        >
                            {response && response.message}
                            {errorMesssage}
                        </MessageTypography>
                    </Box>
                    <Box>
                        <Grid container spacing={2}>
                            <Grid item>
                                <CustomButton
                                    label={
                                        actions &&
                                        actions[1] &&
                                        actions[1].label
                                            ? actions[1].label
                                            : t('save')
                                    }
                                    onClick={() => validateOnSubmit()}
                                    active={true}
                                    disabled={loading || !unsavedChanges}
                                    loading={loading}
                                    data-ai-explain="Save out-of-office settings"
                                />
                            </Grid>

                            <Grid item>
                                <CustomButton
                                    label={
                                        actions &&
                                        actions[1] &&
                                        actions[1].label
                                            ? actions[1].label
                                            : unsavedChanges
                                            ? t('DiscardChanges')
                                            : t('Close')
                                    }
                                    width={150}
                                    onClick={
                                        unsavedChanges ? handleDiscard : onClose
                                    }
                                    active={
                                        actions &&
                                        actions[0] &&
                                        actions[0].active
                                    }
                                    disabled={loading}
                                    data-ai-explain={
                                        unsavedChanges
                                            ? 'Discard unsaved changes'
                                            : 'Close this dialog'
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </StyledModalContentWrapper>
            </StyledModal>
        </div>
    )
}
